import React, { FC } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import { Store } from 'redux';
// @fenderdigital
import Cognito, { CognitoCredentials } from '@fenderdigital/custom-hooks/lib/cognito';
import isDomUsable from '@fenderdigital/utils/dom';
import { UserContextProvider } from '@fenderdigital/custom-hooks/contexts/UserContext';
// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
// @ts-ignore TODO add defs for analytics
import { trackAuthUser } from '@fenderdigital/utils/analytics';
// contexts
import ContextsProvider from '../contexts/Contexts';
import CountriesProvider from '../contexts/CountriesContext';
import PathProvider from '../contexts/PathContext';
import { ModalContextProvider } from '../contexts/ModalContext';
// components
import DeveloperConsole from './DeveloperConsole';
import Layout from './Layout';
import PlayAuthRedirectRoute from './PlayAuthRedirectRoute';
import Styles from './Styles';
// routes
import CollectionsDetail from '../routes/collections/CollectionsDetail';
import Course from '../routes/course/components/Course';
import FeedbackMode from '../routes/feedback-mode';
import LessonContainer from '../routes/lesson/components/LessonContainer';
import NotFound from '../routes/404/NotFound';
import PracticeContainer from '../routes/practice/components/PracticeSheetContainer';
import routes from '../routes';
// lib
import { getFlag } from '../lib/feature-flags';
// config
import { AuthConfig, userApiConfig } from '../config';
// core
import bugsnagClient from '../core/bugsnag';
// store
import applyFeatureFlags from '../store/apply-feature-flags';

const isPlayAuthRedirectEnabled = getFlag('isPlayAuthRedirectEnabled');

const getProviders = (): JSX.Element[] => {
  const providers: JSX.Element[] = [<ModalContextProvider />, <PathProvider />];
  if (!isPlayAuthRedirectEnabled) {
    providers.unshift(<CountriesProvider />);
  }
  return providers;
};

const siteId = process.env.REACT_APP_CLIENT_SITE_ID || 'play';

export interface AppProps {
  queryParams: URLSearchParams;
  store: Store;
}

const CognitoInstance = new Cognito(
  AuthConfig,
  `${process.env.REACT_APP_CLIENT_COGNITO_CLIENT_ID_LAUSD}`,
);
// TODO: post app-split launch, look into if we stil need check
// cognito creds here.
// First page load track event
CognitoInstance.fetchCognitoCredentials().then(
  (response: CognitoCredentials | null): void => {
    let id: string | undefined;
    if (response) {
      id = response.userID;
      bugsnagClient.user = { id };
    } else {
      // Unauthed page track call;
      isDomUsable() && window.analytics.page();
    }
    // since this was initially needed prior to the creationg of
    // play-auth, let's wrap it in a FF to prevent redundant tacking calls
    // this same call exists within the App.tsx of play-auth, see:
    // https://github.com/fenderdigital/fender-clients/blob/main/apps/play-auth/src/components/App.tsx#L56
    if (!isPlayAuthRedirectEnabled) {
      trackAuthUser(siteId, {
        id,
        identitySource: 'Fender Play',
      });
    }
  },
  (): void => {},
);

const App: FC<AppProps> = ({ queryParams, store }) => {
  applyFeatureFlags();
  const state = store && store.getState();
  const intl = (state && state.intl) || {};
  const { locale, messages } = intl;
  const localeMessages = (messages && messages[locale]) || {};
  const PROVIDERS = getProviders();
  return (
    <IntlProvider
      initialNow={Date.now()}
      locale="en-US"
      messages={localeMessages}
      defaultLocale="en-US"
    >
      <UserContextProvider apiConfig={userApiConfig} cognito={CognitoInstance}>
        <ContextsProvider providers={PROVIDERS}>
          <DeveloperConsole>
            <Styles>
              <Layout>
                <Switch>
                  <Route
                    exact
                    path={['/lesson', '/lessons', '/course', '/courses', '/practice']}
                    render={() => <Redirect to="/" />}
                  />
                  <Route exact path="/artists" render={() => <Redirect to="/guitar/songs" />} />
                  <Route
                    exact
                    path={['/skills', '/skills/featured']}
                    render={() => <Redirect to="/guitar/skills" />}
                  />
                  <Route
                    exact
                    path="/:instrument(bass|guitar|ukulele)"
                    render={({ match }) => <Redirect to={`/${match.params.instrument}/songs`} />}
                  />
                  <Route
                    exact
                    path="/:instrument(bass|guitar|ukulele)/artists/*"
                    render={({ match }) => <Redirect to={`/${match.params.instrument}/songs`} />}
                  />
                  <Route
                    exact
                    path="/:instrument(bass|guitar|ukulele)/skills/all/catalog"
                    render={({ match }) => (
                      <Redirect
                        to={`/search/results?query=&instrument=${match.params.instrument}&type=skill`}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/:instrument(bass|guitar|ukulele)/:type(songs|riffs)/:genre(catalog|rock
                    |country|pop|blues|folk|funk)"
                    render={({ match }) => (
                      <Redirect
                        to={`/${match.params.instrument}/${match.params.type}/all/${match.params.genre}`}
                      />
                    )}
                  />
                  <Route exact path="/my-path" render={() => <Redirect to="/path" />} />
                  <Route exact path="/songs/catalog" render={() => <Redirect to="/songs" />} />
                  <Route exact path="/onboarding" render={() => <Redirect to="/plans" />} />
                  <Route
                    exact
                    path={['/onboarding/signin-reseller', '/onboarding/create-password']}
                    render={() => {
                      if (queryParams.has('email')) {
                        const redirSearchParams = new URLSearchParams();
                        redirSearchParams.append('email', queryParams.get('email') || '');
                        redirSearchParams.append('token', queryParams.get('token') || '');
                        return (
                          <Redirect
                            to={{ pathname: '/signin', search: redirSearchParams.toString() }}
                          />
                        );
                      }
                      return <Redirect to="/plans" />;
                    }}
                  />
                  <Route
                    exact
                    path="/onboarding/renew"
                    render={() => <Redirect to={{ pathname: '/plans', search: '?signin=1' }} />}
                  />
                  <Route
                    exact
                    path="/onboarding/:subroute"
                    render={() => <Redirect to="/plans" />}
                  />
                  <Route
                    exact
                    path="/:instrument(bass|guitar|ukulele)/collections/:collectionSlug"
                    render={props => <CollectionsDetail slug={props.match.params.collectionSlug} />}
                  />
                  <PlayAuthRedirectRoute
                    path={[
                      '/lesson/:lessonSlug',
                      '/course/:courseSlug/lesson/:lessonSlug',
                      '/path/:pathSlug/:level/course/:courseSlug/lesson/:lessonSlug',
                      '/:instrument(bass|guitar|ukulele)/collections/:collectionSlug/course/:courseSlug/lesson/:lessonSlug',
                    ]}
                    key="lesson"
                    Component={LessonContainer}
                  />
                  <PlayAuthRedirectRoute
                    path={[
                      '/practice/:practiceSlug',
                      '/course/:courseSlug/practice/:practiceSlug',
                      '/path/:pathSlug/:level/course/:courseSlug/practice/:practiceSlug',
                      '/:instrument(bass|guitar|ukulele)/collections/:collectionSlug/course/:courseSlug/practice/:practiceSlug',
                    ]}
                    key="practice"
                    Component={PracticeContainer}
                  />

                  {getFlag('isMMS') && (
                    <PlayAuthRedirectRoute
                      path={[
                        '/course/:courseSlug/feedback-mode',
                        '/path/:pathSlug/:level/course/:courseSlug/feedback-mode/',
                        '/:instrument(bass|guitar|ukulele)/collections/:collectionSlug/course/:courseSlug/feedback-mode',
                      ]}
                      key="feedback-mode"
                      Component={FeedbackMode}
                    />
                  )}
                  <PlayAuthRedirectRoute
                    path={[
                      '/course/:courseSlug',
                      '/path/:pathSlug/:level/course/:courseSlug',
                      '/:instrument(bass|guitar|ukulele)/collections/:collectionSlug/course/:courseSlug',
                    ]}
                    key="course"
                    Component={Course}
                  />

                  {routes.map(({ path, Component, exact }) => (
                    <PlayAuthRedirectRoute
                      key={path}
                      path={path}
                      Component={Component}
                      exact={exact}
                    />
                  ))}
                  <Route
                    exact
                    path="/:instrument(bass|guitar|ukulele)/:type"
                    render={({ match }) => <Redirect to={`/${match.params.instrument}/songs`} />}
                  />
                  {/* Client Redirects */}
                  <Route
                    exact
                    path="/myaccount"
                    render={() => {
                      if (isDomUsable()) {
                        window.location.replace(
                          `${process.env.REACT_APP_CLIENT_FENDER_COM}/profile/info`,
                        );
                      }
                      return null;
                    }}
                  />
                  <Route
                    exact
                    path="/guitar/skills/catalog"
                    render={() => <Redirect to="/guitar/skills/courses" />}
                  />
                  <Route
                    exact
                    path="/ukulele/skills/catalog"
                    render={() => <Redirect to="/ukulele/skills/courses" />}
                  />
                  <Route
                    exact
                    path="/password-update-article"
                    render={() => {
                      if (isDomUsable()) {
                        window.location.replace(
                          `https://play-support.fender.com/hc/en-us/articles/360021245212`,
                        );
                      }
                      return null;
                    }}
                  />
                  <Route
                    exact
                    path="/password-update"
                    render={() => {
                      if (isDomUsable()) {
                        window.location.replace(
                          `${process.env.REACT_APP_CLIENT_FENDER_COM}/forgot-password`,
                        );
                      }
                      return null;
                    }}
                  />
                  <Route
                    exact
                    path="/offerterms"
                    render={() => {
                      if (isDomUsable()) {
                        window.location.replace(
                          `https://play-support.fender.com/hc/en-us/articles/360015185312`,
                        );
                      }
                      return null;
                    }}
                  />
                  <Route
                    exact
                    path="/tracks"
                    render={() => {
                      if (isDomUsable()) {
                        window.location.replace(`https://soundcloud.com/fenderplaytracks`);
                      }
                      return null;
                    }}
                  />
                  <Route
                    exact
                    path="/announcements"
                    render={() => <Redirect to="/pages/announcements" />}
                  />
                  <Route
                    exact
                    path="/signin"
                    render={() => {
                      if (isDomUsable()) {
                        window.location.replace(`${process.env.REACT_APP_CLIENT_SSO_URL}/sign-in`);
                      }
                      return null;
                    }}
                  />
                  <Route
                    exact
                    path="/signup"
                    render={() => {
                      if (isDomUsable()) {
                        window.location.replace(`${process.env.REACT_APP_CLIENT_SSO_URL}/sign-up`);
                      }
                      return null;
                    }}
                  />
                  <Route
                    exact
                    path="/affiliate"
                    render={() => <Redirect to="/pages/affiliate-program" />}
                  />
                  <Route
                    exact
                    path="/support"
                    render={() => {
                      if (isDomUsable()) {
                        window.location.replace(`https://play-support.fender.com/hc/en-us`);
                      }
                      return null;
                    }}
                  />
                  <Route component={NotFound} />
                </Switch>
              </Layout>
            </Styles>
          </DeveloperConsole>
        </ContextsProvider>
      </UserContextProvider>
    </IntlProvider>
  );
  /* eslint-enable @typescript-eslint/explicit-function-return-type */
};

export default App;
